/**
 * RitualsCommunitiesSection Component
 * Section 7: Tabbed content area with Practicing, Teaching, Sessions, Saved, Communities, Followers.
 * Adapts the existing profile tab pattern for the My Rituality dashboard context.
 */

import { Search, X } from "lucide-react"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useMyCoachingSessions } from "@/features/coaching/hooks/useCoachingMutations"
import { CommunitiesTabContent } from "@/features/profile/components/CommunitiesTabContent"
import { SavedRitualsTabContent } from "@/features/profile/components/SavedRitualsTabContent"
import type { ProfileStatistics } from "@/features/profile/types/profile.types"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import type { FollowersSubTab, MyRitualityTab } from "../types/my-rituality.types"
import { FollowersSubTabs } from "./FollowersSubTabs"
import { PracticingSubTabs } from "./PracticingSubTabs"
import { SessionsSubTabs } from "./SessionsSubTabs"
import { TeachingSubTabs } from "./TeachingSubTabs"

interface RitualsCommunitiesSectionProps {
  userId: string
  statistics?: ProfileStatistics | null
}

export interface RitualsCommunitiesSectionHandle {
  scrollToTab: (tab: MyRitualityTab, subTab?: string) => void
}

export const RitualsCommunitiesSection = forwardRef<
  RitualsCommunitiesSectionHandle,
  RitualsCommunitiesSectionProps
>(function RitualsCommunitiesSectionInner({ userId, statistics }, ref) {
  const { t } = useTranslation("myRituality")
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionRef = useRef<HTMLElement>(null)
  const validSubTabs: FollowersSubTab[] = ["followers", "following"]
  const rawSubTab = searchParams.get("subTab")
  const activeSubTab =
    rawSubTab && validSubTabs.includes(rawSubTab as FollowersSubTab)
      ? (rawSubTab as FollowersSubTab)
      : null
  const [activeTab, setActiveTab] = useState<MyRitualityTab>(() => {
    const tabParam = searchParams.get("tab")
    if (tabParam === "following") {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev)
        newParams.set("tab", "followers")
        newParams.set("subTab", "following")
        return newParams
      })
      return "followers"
    }
    return (tabParam as MyRitualityTab) || "practicing"
  })
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  // Update URL when tab changes
  const handleTabChange = (value: MyRitualityTab) => {
    setActiveTab(value)
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      newParams.set("tab", value)
      newParams.delete("subTab")
      return newParams
    })
  }

  useImperativeHandle(ref, () => ({
    scrollToTab(tab, subTab) {
      setActiveTab(tab)
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev)
        newParams.set("tab", tab)
        if (subTab) {
          newParams.set("subTab", subTab)
        } else {
          newParams.delete("subTab")
        }
        return newParams
      })
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    },
  }))

  // Scroll to section when tab is set via URL params (initial load only)
  useEffect(() => {
    const tabParam = searchParams.get("tab")
    const subTabParam = searchParams.get("subTab")
    if (tabParam === "followers" && subTabParam) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    }
  }, [searchParams])

  // Fetch coaching sessions count separately for accurate Sessions tab count
  const { data: coachingSessionsData } = useMyCoachingSessions(userId, { limit: 1 })

  const tabTriggerClass =
    "!flex-none !rounded-none !h-[65px] !px-5 !text-[15px] !font-semibold !uppercase !whitespace-nowrap !leading-[25px] !cursor-pointer !border-0 !border-b !bg-transparent after:!hidden data-[state=active]:!text-text-primary data-[state=active]:!border-text-primary data-[state=inactive]:!text-text-primary/50 data-[state=inactive]:!border-white/20"

  const tabs: { value: MyRitualityTab; label: string; count: number }[] = [
    { value: "practicing", label: t("tabs.practicing"), count: statistics?.practicing ?? 0 },
    { value: "teaching", label: t("tabs.teaching"), count: statistics?.teachings ?? 0 },
    { value: "sessions", label: t("tabs.sessions"), count: coachingSessionsData?.total ?? 0 },
    { value: "saved", label: t("tabs.saved"), count: statistics?.savedRituals ?? 0 },
    { value: "communities", label: t("tabs.communities"), count: statistics?.communities ?? 0 },
    { value: "followers", label: t("tabs.followers"), count: statistics?.followersCount ?? 0 },
  ]

  return (
    <section ref={sectionRef}>
      {/* Section heading */}
      <h2 className="text-lg md:text-2xl font-semibold leading-7.5 uppercase opacity-90 text-primary mb-2.5 md:mb-5 max-w-360 px-5 md:px-17.5 mx-auto">
        {t("tabs.title")}
      </h2>

      <Tabs value={activeTab} onValueChange={v => handleTabChange(v as MyRitualityTab)}>
        {/* Tab bar */}
        <div className="flex items-center max-w-360 px-5 md:px-17.5 mx-auto w-full overflow-x-auto scrollbar-hide">
          <TabsList
            variant="default"
            className="!h-auto !gap-0 p-0 flex-row bg-transparent shrink-0"
          >
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={cn(tabTriggerClass, "shrink-0")}
              >
                <span className="whitespace-nowrap">{tab.label} </span>
                <span className="font-normal whitespace-nowrap">({tab.count})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Spacer with bottom border */}
          <div className="flex-1 h-[65px] border-b border-white/20" />

          {/* Search toggle */}
          <div className="hidden md:flex items-center h-[65px] border-b border-white/20 shrink-0">
            {showSearch ? (
              <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5">
                <Search className="w-4 h-4 text-primary/50 shrink-0" />
                <Input
                  type="text"
                  placeholder={t("tabs.search")}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-48 border-none bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-6 w-6 text-primary/50"
                  onClick={() => {
                    setShowSearch(false)
                    setSearchQuery("")
                  }}
                  aria-label={t("tabs.closeSearch")}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className="opacity-50 cursor-pointer"
                onClick={() => setShowSearch(true)}
                aria-label={t("tabs.search")}
              >
                <Search className="w-5 h-5 text-primary" />
              </button>
            )}
          </div>
        </div>

        {/* Tab content area - dark background */}
        <div className="bg-transparent md:bg-my-rituality-content-bg">
          <div className="py-5 max-w-360 md:px-17.5 mx-auto">
            <TabsContent value="practicing">
              <PracticingSubTabs userId={userId} searchQuery={searchQuery} />
            </TabsContent>
            <TabsContent value="teaching">
              <TeachingSubTabs userId={userId} searchQuery={searchQuery} />
            </TabsContent>
            <TabsContent value="sessions">
              <SessionsSubTabs
                userId={userId}
                searchQuery={searchQuery}
                coachings={statistics?.coachings}
              />
            </TabsContent>
            <TabsContent value="saved">
              <SavedRitualsTabContent searchQuery={searchQuery} className="px-5 md:px-0" />
            </TabsContent>

            <TabsContent value="communities">
              <CommunitiesTabContent
                totalCount={statistics?.communities ?? 0}
                userId={userId}
                searchQuery={searchQuery}
                showEnhancedEmpty
              />
            </TabsContent>

            <TabsContent value="followers">
              <FollowersSubTabs
                userId={userId}
                statistics={statistics}
                searchQuery={searchQuery}
                initialSubTab={activeSubTab ?? undefined}
              />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </section>
  )
})
